import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { ContentPageShell, Prose } from "@/components/ContentPageShell";

export default function ScoringRules() {
  return (
    <>
      <PageMeta
        title="Bodovací pravidla — Tipomatic"
        description="Jak Tipomatic boduje tipy: přesný výsledek, správná tendence, skupinové tipy a bonus za unikátní přesný tip."
        path="/scoring-rules"
      />
      <ContentPageShell>
        <h1 className="text-3xl md:text-4xl font-bold mb-6">Bodovací pravidla</h1>
        <Prose>
          <p>
            Tipomatic používá jednoduchý a férový bodovací systém. Základní hodnoty si
            nastavuje admin ligy podle vlastního uvážení, ale logika je ve všech ligách
            stejná. Tato stránka popisuje výchozí bodování tak, jak jej aplikace skutečně počítá.
          </p>

          <h2>Bodování jednotlivých zápasů</h2>
          <p>Za každý tip zápasu můžete získat body ve dvou úrovních:</p>
          <ol>
            <li>
              <strong>Přesný výsledek</strong> — natipovali jste 2:1 a zápas skončil 2:1.
              Výchozí hodnota: <strong>3 body</strong> (v aplikaci pole
              <em> exact_score_points</em>).
            </li>
            <li>
              <strong>Správná tendence</strong> — trefili jste jen vítěze (nebo remízu), ne přesné skóre.
              Výchozí hodnota: <strong>1 bod</strong> (pole <em>correct_outcome_points</em>).
            </li>
          </ol>
          <p>
            Když tendenci netrefíte, získáváte <strong>0 bodů</strong>. Za přesný výsledek
            dostáváte plný počet bodů — nesčítá se s bodem za tendenci.
          </p>

          <h2>Bod za správný počet gólů</h2>
          <p>
            Admin ligy může zapnout bonus za <strong>správný počet gólů jednoho týmu</strong>
            (výchozí 0 = vypnuto, doporučeně 1 bod). Připočte se, když jste trefili počet gólů
            alespoň u jednoho týmu, ale netrefili přesný výsledek. S body za přesný výsledek se
            nesčítá.
          </p>

          <h2>Speciální tipy</h2>
          <p>
            Kromě zápasů může liga nabídnout <strong>speciální tipy</strong> — například nejlepší
            a nejhorší tým základní části, tým s nejvíce vstřelenými nebo obdrženými góly,
            největší rozdíl ve skóre zápasu nebo nejvíce gólů v jednom zápase. Otázky zakládá
            správce turnaje, admin ligy je může zapnout či vypnout a nastavit u nich počet bodů
            (výchozí 2 body). Odpovědi se zpravidla <strong>uzamykají startem prvního zápasu</strong>
            turnaje, případně ve vlastním termínu. Získané body se sčítají s body za zápasy
            i skupinové tipy. Podrobný rozbor včetně příkladů najdete na stránce <Link to="/special-bets">Speciální tipy</Link>.
          </p>

          <h2>Bonus za unikátní přesný tip (prémiové ligy)</h2>
          <p>
            V prémiových ligách získáváte navíc <strong>bonus +1 bod</strong>, pokud jste
            přesný výsledek trefili jako <strong>jediný</strong> hráč v celé lize. Odměňuje
            odvahu tipovat méně obvyklá skóre. V neprémiových ligách se bonus nepočítá.
          </p>

          <h2>Bodování skupinové fáze</h2>
          <p>
            Před začátkem turnaje můžete v každé skupině natipovat, kdo skončí na
            <strong> 1. a 2. místě</strong>. Vyhodnocení funguje takto:
          </p>
          <ul>
            <li>
              <strong>Správné pořadí</strong> — natipovaný tým skončil přesně na 1. nebo 2. místě
              (výchozí <strong>2 body</strong> za tým, pole <em>group_correct_position_points</em>).
            </li>
            <li>
              <strong>Trefený postupující</strong> — tým skončil v TOP2, ale na jiném místě než jste tipovali
              (výchozí <strong>1 bod</strong> za tým, pole <em>group_correct_advancing_points</em>).
            </li>
          </ul>
          <p>
            Body za skupinové tipy se sčítají s body za jednotlivé zápasy.
          </p>

          <h2>Vyřazovací fáze</h2>
          <p>
            Ve vyřazovací fázi Tipomatic tipuje výsledek <strong>v základní hrací době</strong>.
            Pokud zápas dojde do prodloužení nebo penalt, pro vyhodnocení tipu se počítá
            skóre v 90. minutě. Zápasy playoff se bodují stejnými pravidly jako skupinová
            fáze — <strong>žádný násobič</strong> se nepoužívá.
          </p>

          <h2>Tie-break — rovnost bodů</h2>
          <p>
            Pokud mají dva hráči stejný počet bodů, rozhoduje o pořadí:
          </p>
          <ol>
            <li>celkový počet bodů (víc = lepší),</li>
            <li>počet <strong>přesných výsledků</strong> (víc = lepší),</li>
            <li>interní stabilní pořadí (aby bylo pořadí deterministické).</li>
          </ol>

          <h2>Příklad výpočtu</h2>
          <p>Zápas Anglie – Francie skončil <strong>2:1</strong>. Při výchozím nastavení (3 / 1):</p>
          <ul>
            <li><strong>Petr</strong> tipoval 2:1 → přesný výsledek → <strong>3 body</strong>.</li>
            <li><strong>Marie</strong> tipovala 3:2 → trefila vítěze → <strong>1 bod</strong>.</li>
            <li><strong>Honza</strong> tipoval 1:0 → trefil vítěze → <strong>1 bod</strong>.</li>
            <li><strong>Eva</strong> tipovala 1:1 → tendence netrefena → <strong>0 bodů</strong>.</li>
          </ul>
          <p>
            Pokud by byla liga prémiová a Petr byl jediný s tipem 2:1, dostane navíc
            <strong> +1 bod</strong> za unikátní přesný tip — celkem 4 body.
          </p>

          <h2>Změna pravidel během turnaje</h2>
          <p>
            Admin ligy může pravidla teoreticky upravit i uprostřed turnaje — všechny už
            zadané tipy se automaticky <strong>přepočítají</strong> podle nových hodnot, aby
            bylo pořadí férové. Přesto doporučujeme pravidla po startu turnaje neměnit —
            vede to k frustraci hráčů.
          </p>

          <h2>Detailní rozpad na obrazovce zápasu</h2>
          <p>
            Po odpískání zápasu si v jeho detailu můžete zobrazit tipy všech hráčů a body,
            které za ně dostali. Ideální podklad do skupinového chatu.
          </p>

          <p className="pt-4 border-t border-white/10">
            Něco nejasného? Napište nám přes <Link to="/contact">kontaktní stránku</Link>.
          </p>
        </Prose>
      </ContentPageShell>
    </>
  );
}
